import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ClientSessionService } from '@brighthub/uisession';
import { CopilotContextComposerService } from '@brighthub/copilot';
import { ClientAccountsStateService } from './client-accounts-state.service';
import { ColResizeDirective } from '../client-actions/col-resize.directive';
import {
  FilterChipGroupComponent,
  FilterChipOption,
} from '../shared/filter-chip-group/filter-chip-group.component';
import { AccountDetailDialogComponent } from './account-detail-dialog/account-detail-dialog.component';
import {
  AccountInsight,
  AccountInsightSeverity,
  AccountRisk,
  AssetAccount,
  ClientAccountsState,
  LiabilityAccount,
} from './client-accounts.model';

export type AccountFilterChip = 'all' | 'assets' | 'liabilities' | 'attention' | 'recently-updated';
export type AccountSortBy = 'balance' | 'apr' | 'utilization' | 'ytd' | 'lastUpdated';
export type AccountSortDir = 'asc' | 'desc';

export interface AccountInventoryRow {
  id: string;
  kind: 'asset' | 'liability';
  account: AssetAccount | LiabilityAccount;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const PCT = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 });
const APR_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CATEGORY_LABELS: Record<string, string> = {
  checking: 'Checking',
  savings: 'Savings',
  investment: 'Investment',
  retirement: 'Retirement',
  real_estate: 'Real Estate',
  other_asset: 'Other Asset',
  credit_card: 'Credit Card',
  student_loan: 'Student Loan',
  medical_debt: 'Medical Debt',
  personal_loan: 'Personal Loan',
  auto_loan: 'Auto Loan',
  mortgage: 'Mortgage',
  other_liability: 'Other Liability',
};

const ACCOUNT_FILTER_OPTIONS: FilterChipOption[] = [
  { id: 'all', label: 'All' },
  { id: 'assets', label: 'Assets' },
  { id: 'liabilities', label: 'Liabilities' },
  { id: 'attention', label: 'Needs Attention' },
  { id: 'recently-updated', label: 'Recent Updates' },
];

@Component({
  selector: 'bai-client-accounts',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ColResizeDirective,
    FilterChipGroupComponent,
    FormsModule,
    SkeletonModule,
    TagModule,
  ],
  providers: [DialogService],
  templateUrl: './client-accounts.component.html',
  styleUrl: './client-accounts.component.scss',
})
export class ClientAccountsComponent {
  private readonly _session = inject(ClientSessionService);
  private readonly _stateService = inject(ClientAccountsStateService);
  private readonly _dialogService = inject(DialogService);
  private readonly _copilotComposer = inject(CopilotContextComposerService);
  private _dialogRef: DynamicDialogRef | null = null;

  readonly loading = signal(true);

  readonly searchQuery = signal('');
  readonly activeFilter = signal<AccountFilterChip>('all');
  readonly accountFilterOptions = ACCOUNT_FILTER_OPTIONS;
  readonly sortBy = signal<AccountSortBy>('lastUpdated');
  readonly sortDir = signal<AccountSortDir>('desc');
  readonly selectedAccountId = signal<string | null>(null);

  private readonly _sessionId = computed(
    () => this._session.selectedSession()?.uiSessionId ?? null
  );

  constructor() {
    effect(() => {
      const id = this._sessionId();
      if (id) {
        this._stateService.ensureState(id);
      }
      this.loading.set(false);
    });

    effect(() => {
      const rows = this.inventoryRows();
      const selectedId = this.selectedAccountId();

      if (rows.length === 0) {
        if (selectedId !== null) {
          this.selectedAccountId.set(null);
        }
        return;
      }

      const selectionExists = selectedId
        ? rows.some((row) => row.id === selectedId)
        : false;

      if (!selectionExists) {
        this.selectedAccountId.set(rows[0].id);
      }
    });
  }

  readonly state = computed((): ClientAccountsState | null => {
    const id = this._sessionId();
    if (!id) return null;
    return this._stateService.storeSnapshot()[id] ?? null;
  });

  readonly allRows = computed((): AccountInventoryRow[] => {
    const s = this.state();
    if (!s) return [];

    const assets: AccountInventoryRow[] = s.assets.map((asset) => ({
      id: `asset:${asset.id}`,
      kind: 'asset',
      account: asset,
    }));

    const liabilities: AccountInventoryRow[] = s.liabilities.map((liability) => ({
      id: `liability:${liability.id}`,
      kind: 'liability',
      account: liability,
    }));

    return [...assets, ...liabilities];
  });

  readonly inventoryRows = computed((): AccountInventoryRow[] => {
    let rows = [...this.allRows()];
    const query = this.searchQuery().toLowerCase().trim();
    const filter = this.activeFilter();
    const sortBy = this.sortBy();
    const sortDir = this.sortDir();

    if (filter === 'assets') {
      rows = rows.filter((row) => row.kind === 'asset');
    }

    if (filter === 'liabilities') {
      rows = rows.filter((row) => row.kind === 'liability');
    }

    if (filter === 'attention') {
      rows = rows.filter((row) => this.rowNeedsAttention(row));
    }

    if (filter === 'recently-updated') {
      rows = rows.filter((row) => this._daysAgo(row.account.lastUpdated) <= 30);
    }

    if (query) {
      rows = rows.filter((row) => {
        const account = row.account;
        return (
          account.name.toLowerCase().includes(query) ||
          account.institution.toLowerCase().includes(query) ||
          account.last4.includes(query) ||
          this.categoryLabel(account.category).toLowerCase().includes(query)
        );
      });
    }

    rows.sort((a, b) => {
      const valueA = this._sortValue(a, sortBy);
      const valueB = this._sortValue(b, sortBy);

      let compare = 0;
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        compare = valueA - valueB;
      } else {
        compare = String(valueA).localeCompare(String(valueB));
      }

      return sortDir === 'desc' ? -compare : compare;
    });

    return rows;
  });

  readonly selectedRow = computed((): AccountInventoryRow | null => {
    const rows = this.inventoryRows();
    const selectedId = this.selectedAccountId();
    if (!rows.length || !selectedId) return null;
    return rows.find((row) => row.id === selectedId) ?? null;
  });

  readonly totalAccountCount = computed((): number => this.allRows().length);

  readonly attentionCount = computed((): number =>
    this.allRows().filter((row) => this.rowNeedsAttention(row)).length
  );

  readonly staleCount = computed((): number =>
    this.allRows().filter((row) => this.isStale(row.account.lastUpdated)).length
  );

  readonly selectedAccountInsights = computed((): AccountInsight[] => {
    const selected = this.selectedRow();
    const s = this.state();
    if (!selected || !s) return [];

    const name = selected.account.name.toLowerCase();
    const institution = selected.account.institution.toLowerCase();

    const targeted = s.insights.filter((insight) => {
      const title = insight.title.toLowerCase();
      const body = insight.body.toLowerCase();
      return (
        title.includes(name) ||
        body.includes(name) ||
        title.includes(institution) ||
        body.includes(institution)
      );
    });

    if (targeted.length > 0) {
      return targeted;
    }

    return this._fallbackInsightsForAccount(selected);
  });

  readonly selectedAccountActions = computed((): string[] => {
    const selected = this.selectedRow();
    if (!selected) return [];

    const actions: string[] = [];

    if (this.isLiability(selected.account)) {
      const account = selected.account;

      if (account.apr !== null && account.apr > 20) {
        actions.push('Discuss refinance or balance transfer options to lower APR exposure.');
      }

      if (account.utilization !== null && account.utilization > 0.5) {
        actions.push('Prioritize a utilization paydown plan to reduce credit pressure.');
      }

      if (account.minPayment !== null) {
        actions.push(`Confirm autopay or manual payment path for minimum payment of ${this.formatUsd(account.minPayment)}.`);
      }

      if (account.ytdChangeDollar > 0) {
        actions.push('Debt balance is increasing this year; align on an immediate reduction target.');
      }
    } else {
      const account = selected.account;

      if (account.ytdChangeDollar < 0) {
        actions.push('Asset balance is down YTD; review if withdrawals were planned or require intervention.');
      } else {
        actions.push('Asset growth is positive YTD; capture this as a stability signal in case notes.');
      }
    }

    if (this.isStale(selected.account.lastUpdated)) {
      actions.push('Request fresh account data before making eligibility or affordability recommendations.');
    }

    if (actions.length === 0) {
      actions.push('No immediate intervention required; continue normal monitoring cadence.');
    }

    return actions;
  });

  formatUsd(value: number): string {
    return USD.format(value);
  }

  formatPct(value: number): string {
    return PCT.format(value);
  }

  formatApr(value: number): string {
    return APR_FMT.format(value);
  }

  formatYtdDollar(value: number): string {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${USD.format(value)}`;
  }

  formatYtdPct(value: number): string {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  }

  categoryLabel(category: string): string {
    return CATEGORY_LABELS[category] ?? category;
  }

  accountKindLabel(kind: 'asset' | 'liability'): string {
    return kind === 'asset' ? 'Asset' : 'Liability';
  }

  riskSeverity(risk: AccountRisk): 'success' | 'warn' | 'danger' | 'secondary' {
    if (risk === 'healthy') return 'success';
    if (risk === 'caution') return 'warn';
    if (risk === 'risk') return 'danger';
    return 'secondary';
  }

  riskLabel(risk: AccountRisk): string {
    const labels: Record<AccountRisk, string> = {
      healthy: 'Healthy',
      caution: 'Caution',
      risk: 'Risk',
      neutral: 'Neutral',
    };
    return labels[risk];
  }

  insightSeverityClass(severity: AccountInsightSeverity): string {
    if (severity === 'danger') return 'bai-acc-insight--danger';
    if (severity === 'warn') return 'bai-acc-insight--warn';
    return 'bai-acc-insight--info';
  }

  insightTagSeverity(severity: AccountInsightSeverity): 'danger' | 'warn' | 'info' {
    return severity === 'danger' ? 'danger' : severity === 'warn' ? 'warn' : 'info';
  }

  isStale(lastUpdated: string): boolean {
    return this._daysAgo(lastUpdated) > 90;
  }

  selectAccount(id: string): void {
    this.selectedAccountId.set(id);
  }

  onFilterSelection(selection: string | string[]): void {
    if (Array.isArray(selection)) return;
    if (
      this.accountFilterOptions.some((option) => option.id === selection)
    ) {
      this.activeFilter.set(selection as AccountFilterChip);
    }
  }

  openSelectedAccountDetail(): void {
    const selected = this.selectedRow();
    if (!selected) return;
    this.openAccountDetail(selected.account);
  }

  openAccountDetail(account: AssetAccount | LiabilityAccount): void {
    this._dialogRef?.close();
    this._dialogRef = this._dialogService.open(AccountDetailDialogComponent, {
      header: account.name,
      data: account,
      width: '480px',
      modal: true,
      dismissableMask: true,
      closeOnEscape: true,
    });
  }

  rowRiskSeverity(row: AccountInventoryRow): 'success' | 'warn' | 'danger' | 'info' {
    if (this.isLiability(row.account)) {
      if (row.account.risk === 'risk') return 'danger';
      if (row.account.risk === 'caution') return 'warn';
      return this.isStale(row.account.lastUpdated) ? 'warn' : 'success';
    }

    if (this.isStale(row.account.lastUpdated)) return 'warn';
    return 'info';
  }

  rowRiskLabel(row: AccountInventoryRow): string {
    if (this.isLiability(row.account)) {
      return this.riskLabel(row.account.risk);
    }

    return this.isStale(row.account.lastUpdated) ? 'Stale Data' : 'Current';
  }

  rowNeedsAttention(row: AccountInventoryRow): boolean {
    if (this.isStale(row.account.lastUpdated)) return true;

    if (!this.isLiability(row.account)) {
      return row.account.ytdChangeDollar < 0;
    }

    const liability = row.account;
    return (
      liability.risk === 'risk' ||
      liability.risk === 'caution' ||
      (liability.apr !== null && liability.apr > 20) ||
      (liability.utilization !== null && liability.utilization > 0.5) ||
      liability.ytdChangeDollar > 0
    );
  }

  trackRow(_: number, row: AccountInventoryRow): string {
    return row.id;
  }

  trackInsight(_: number, insight: AccountInsight): string {
    return insight.id;
  }

  trackAction(_: number, action: string): string {
    return action;
  }

  addAccountToCopilot(row: AccountInventoryRow): void {
    const account = row.account;
    const liabilityFields = this.isLiability(account)
      ? `APR: ${account.apr !== null ? `${this.formatApr(account.apr)}%` : '—'}; ` +
        `Utilization: ${account.utilization !== null ? this.formatPct(account.utilization) : '—'}; ` +
        `Min Payment: ${account.minPayment !== null ? `${this.formatUsd(account.minPayment)}/mo` : '—'}; `
      : '';

    this._copilotComposer.addContext({
      sourceType: 'accounts-row',
      sourceId: row.id,
      label: `Account: ${this._truncate(account.name, 20)}`,
      fullText:
        `[Accounts | ${this.accountKindLabel(row.kind)} | ${row.id}] ` +
        `Account: ${account.name}; Institution: ${account.institution}; ` +
        `Type: ${this.categoryLabel(account.category)}; Last4: ${account.last4}; ` +
        `Balance: ${this.formatUsd(account.balance)}; ` +
        `${liabilityFields}` +
        `YTD: ${this.formatYtdDollar(account.ytdChangeDollar)} (${this.formatYtdPct(account.ytdChangePct)}); ` +
        `Last Updated: ${account.lastUpdated}; ` +
        `Status: ${this.rowRiskLabel(row)}`,
    });
  }

  addAccountInsightToCopilot(insight: AccountInsight, row: AccountInventoryRow | null): void {
    const sourceId = row ? `${row.id}:${insight.id}` : insight.id;
    const rowLabel = row ? `${row.account.name}` : 'selected account';
    this._copilotComposer.addContext({
      sourceType: 'accounts-insight',
      sourceId,
      label: `Account Insight: ${this._truncate(insight.title, 20)}`,
      fullText:
        `[Accounts | Insight | ${sourceId}] ` +
        `Account: ${rowLabel}; ` +
        `Title: ${insight.title}; ` +
        `Severity: ${insight.severity}; ` +
        `Summary: ${insight.body}`,
    });
  }

  isLiability(account: AssetAccount | LiabilityAccount): account is LiabilityAccount {
    return 'apr' in account;
  }

  private _truncate(value: string, max = 20): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  }

  private _daysAgo(dateStr: string): number {
    return (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24);
  }

  private _sortValue(row: AccountInventoryRow, sortBy: AccountSortBy): number {
    if (sortBy === 'balance') {
      return row.account.balance;
    }

    if (sortBy === 'apr') {
      return this.isLiability(row.account) ? row.account.apr ?? -1 : -1;
    }

    if (sortBy === 'utilization') {
      return this.isLiability(row.account) ? row.account.utilization ?? -1 : -1;
    }

    if (sortBy === 'ytd') {
      return row.account.ytdChangeDollar;
    }

    return new Date(row.account.lastUpdated).getTime();
  }

  private _fallbackInsightsForAccount(row: AccountInventoryRow): AccountInsight[] {
    const account = row.account;

    if (this.isLiability(account)) {
      const liabilityInsights: AccountInsight[] = [
        {
          id: `ins-liab-${row.id}-1`,
          icon: 'pi pi-shield',
          title: 'Liability Monitoring',
          body: `${account.name} requires active payment and utilization tracking to avoid downstream affordability risk.`,
          severity: 'info',
        },
      ];

      if (account.apr !== null && account.apr > 20) {
        liabilityInsights.push({
          id: `ins-liab-${row.id}-2`,
          icon: 'pi pi-percentage',
          title: 'High Interest Exposure',
          body: 'APR is above 20%, which may materially increase carrying cost over the next 90 days.',
          severity: 'danger',
        });
      }

      if (account.utilization !== null && account.utilization > 0.3) {
        liabilityInsights.push({
          id: `ins-liab-${row.id}-3`,
          icon: 'pi pi-chart-line',
          title: 'Utilization Pressure',
          body: `Utilization is ${Math.round(account.utilization * 100)}%. Lowering this can improve underwriting posture.`,
          severity: account.utilization > 0.5 ? 'danger' : 'warn',
        });
      }

      return liabilityInsights;
    }

    return [
      {
        id: `ins-asset-${row.id}-1`,
        icon: 'pi pi-wallet',
        title: 'Asset Liquidity Signal',
        body: `${account.name} can be used as a near-term liquidity signal when prioritizing options in Actions.`,
        severity: 'info',
      },
      {
        id: `ins-asset-${row.id}-2`,
        icon: account.ytdChangeDollar < 0 ? 'pi pi-arrow-down' : 'pi pi-arrow-up',
        title: account.ytdChangeDollar < 0 ? 'Balance Decline Detected' : 'Balance Momentum Positive',
        body:
          account.ytdChangeDollar < 0
            ? 'YTD balance is trending down. Validate if this is expected or if intervention is needed.'
            : 'YTD balance is trending up, which supports stronger account-level stability narratives.',
        severity: account.ytdChangeDollar < 0 ? 'warn' : 'info',
      },
    ];
  }
}
